import { nonAuthInstance } from '../config/axios.config';
import Cookies from 'js-cookie';

const AUTH_ENDPOINTS = {
  LOGIN: '/admin/auth/login',
  REFRESH_TOKEN: '/admin/auth/refresh-token',
  LOGOUT: '/admin/auth/logout',
  FORGOT_PASSWORD: '/admin/auth/forgot-password',
  RESET_PASSWORD: '/admin/auth/reset-password',
};

class AuthService {
  /**
   * Login user with email and password
   * @param {Object} credentials - User credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Login response with user data and tokens
   */
  async login(credentials) {
    const response = await nonAuthInstance.post(AUTH_ENDPOINTS.LOGIN, credentials);
    const { tokens, user } = response.data;

    // Save tokens to cookies
    this.setTokens(tokens);

    // User object (with populated role/permissions) can exceed the ~4KB
    // per-cookie limit and silently fail to write, so store it in
    // localStorage instead of a cookie.
    localStorage.setItem('user', JSON.stringify(user));

    return { user, tokens };
  }

  /**
   * Refresh access token using refresh token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} New access token
   */
  async refreshToken(refreshToken) {
    const response = await nonAuthInstance.post(AUTH_ENDPOINTS.REFRESH_TOKEN, {
      refreshToken,
    });
    const { tokens } = response.data;

    // Update access token
    if (tokens.access) {
      Cookies.set('accessToken', tokens.access.token, {
        expires: tokens.access.expiresIn / 86400,
        secure: window.location.protocol === 'https:',
        sameSite: 'strict',
      });
    }

    return tokens;
  }

  /**
   * Logout user and clear tokens
   */
  logout() {
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }

  /**
   * Save tokens to cookies
   * @param {Object} tokens - Token object containing access and refresh tokens
   */
  setTokens(tokens) {
    const { access, refresh } = tokens;

    // Set access token (expires in seconds, convert to days for cookie)
    Cookies.set('accessToken', access.token, {
      expires: access.expiresIn / 86400,
      secure: window.location.protocol === 'https:',
      sameSite: 'strict',
    });

    // Set refresh token
    Cookies.set('refreshToken', refresh.token, {
      expires: refresh.expiresIn / 86400,
      secure: window.location.protocol === 'https:',
      sameSite: 'strict',
    });
  }

  /**
   * Get access token from cookies
   * @returns {string|null} Access token
   */
  getAccessToken() {
    return Cookies.get('accessToken') || null;
  }

  /**
   * Get refresh token from cookies
   * @returns {string|null} Refresh token
   */
  getRefreshToken() {
    return Cookies.get('refreshToken') || null;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  /**
   * Request password reset email
   * @param {Object} data - Forgot password data
   * @param {string} data.email - User email
   * @param {string} data.domain - Frontend domain for reset link
   * @param {string} data.path - Path for reset password page
   * @returns {Promise<Object>} Response message
   */
  async forgotPassword(data) {
    const response = await nonAuthInstance.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, data);
    return response.data;
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {Object} data - Password reset data
   * @param {string} data.password - New password
   * @param {string} data.confirmPassword - Confirm new password
   * @returns {Promise<Object>} Response message
   */
  async resetPassword(token, data) {
    const response = await nonAuthInstance.patch(`${AUTH_ENDPOINTS.RESET_PASSWORD}/${token}`, data);
    return response.data;
  }
}

export default new AuthService();
